import moment from "moment";
import { useState } from "react";
import { DatePicker } from "rsuite";
import PeoplePicker, { TaskPerson } from "./PeoplePicker";
import "./create.css";

/** The room the due-date calendar needs below its field, in pixels. */
const CALENDAR_ROOM = 300;

/** A task as its form edits it: New task, and a task's inline edit. */
export interface TaskDraft {
  name: string;
  content: string;
  due_date: string;
  /** Who does it. */
  assigned: TaskPerson[];
  /** Who it is about. */
  tagged: TaskPerson[];
}

/**
 * The task form's fields, one column: name, an optional description, the
 * due date, and who it is assigned to and about. New task and a task's
 * inline edit both use it, so the two never drift apart. Put it inside an
 * element with the task-form class, which sizes the pickers. With readOnly
 * the same fields show as text (the People panel's view-task window).
 */
export default function TaskFields({
  idPrefix,
  task,
  onChange,
  people,
  suggestedAssign,
  suggestedAbout,
  dateContainer,
  fitMenusToWindow = false,
  readOnly = false,
}: {
  /** Makes the fields' ids unique on the page. */
  idPrefix: string;
  task: TaskDraft;
  onChange: (task: TaskDraft) => void;
  /** Everyone a task can name (taskPeople). */
  people: TaskPerson[];
  /** Listed first in each picker. */
  suggestedAssign: TaskPerson[];
  suggestedAbout: TaskPerson[];
  /** Where the date picker's calendar opens. */
  dateContainer: () => HTMLElement;
  /**
   * In a pop-out: the pickers' menus fit the window (PeoplePicker), and the
   * calendar opens on the side of its field with room.
   */
  fitMenusToWindow?: boolean;
  /** Show the values as text, not as fields. */
  readOnly?: boolean;
}) {
  // In a pop-out, the calendar opens above its field when there is no room
  // for it below (the pop-out dragged low in the window).
  const [dateUp, setDateUp] = useState(false);

  if (readOnly) {
    const names = (chosen: TaskPerson[]) =>
      chosen.map((person) => person.label).join(", ") || "–";
    return (
      <dl className="task-form__fields task-form__read">
        <div>
          <dt className="input-label">Task name</dt>
          <dd>{task.name}</dd>
        </div>
        <div>
          <dt className="input-label">Description</dt>
          <dd className="whitespace-pre-wrap">{task.content || "–"}</dd>
        </div>
        <div>
          <dt className="input-label">Due date</dt>
          <dd>{moment(task.due_date).format("ddd, MMM D, YYYY")}</dd>
        </div>
        <div>
          <dt className="input-label">Assigned to</dt>
          <dd>{names(task.assigned)}</dd>
        </div>
        <div>
          <dt className="input-label">About</dt>
          <dd>{names(task.tagged)}</dd>
        </div>
      </dl>
    );
  }

  return (
    <div className="task-form__fields">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="input-label">
          Task name
        </label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          className="input"
          value={task.name}
          onChange={(e) => onChange({ ...task, name: e.target.value })}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-description`} className="input-label">
          Description <span className="task-form__optional">Optional</span>
        </label>
        <textarea
          id={`${idPrefix}-description`}
          className="input resize-y"
          rows={3}
          value={task.content}
          onChange={(e) => onChange({ ...task, content: e.target.value })}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-due-date`} className="input-label">
          Due date
        </label>
        <DatePicker
          id={`${idPrefix}-due-date`}
          className="task-form__date"
          oneTap
          cleanable={false}
          format="yyyy-MM-dd"
          placement={dateUp ? "topStart" : "bottomStart"}
          onOpen={() => {
            if (!fitMenusToWindow) return;
            const field = document.getElementById(`${idPrefix}-due-date`);
            if (!field) return;
            const { top, bottom } = field.getBoundingClientRect();
            const below = window.innerHeight - bottom;
            setDateUp(below < CALENDAR_ROOM && top > below);
          }}
          value={new Date(task.due_date)}
          onChange={(date) => {
            if (date && !isNaN(date.getTime())) {
              onChange({ ...task, due_date: date.toISOString() });
            }
          }}
          container={dateContainer}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-assign-to`} className="input-label">
          Assign to
        </label>
        <PeoplePicker
          inputId={`${idPrefix}-assign-to`}
          people={people}
          suggested={suggestedAssign}
          value={task.assigned}
          onChange={(assigned) => onChange({ ...task, assigned })}
          placeholder="Search admins, tutors, parents and students"
          fitMenuToWindow={fitMenusToWindow}
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-about`} className="input-label">
          About <span className="task-form__optional">Optional</span>
        </label>
        <PeoplePicker
          inputId={`${idPrefix}-about`}
          people={people}
          suggested={suggestedAbout}
          value={task.tagged}
          onChange={(tagged) => onChange({ ...task, tagged })}
          placeholder="Who the task is about"
          fitMenuToWindow={fitMenusToWindow}
        />
      </div>
    </div>
  );
}
